import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { IconComponent } from '@/components/IconComponent';
import { amountWithCurrencySymbol } from '@/helpers/currency';
import type { ThemeMode } from '@/helpers/enums';
import { useUserWageringBonuses } from '@/hooks/bonuses';
import { usePlayerAccounts } from '@/modules/common/hooks/usePlayerAccounts';
import { useGetCommonStore } from '@/store';
import { Button } from '../../buttons';
import { EditCurrenciesModal } from '../../Deposit/components/CurrencyBlocks/EditCurrenciesModal';
import { ImageComponent } from '../../Image';
import { ScrollBlock } from '../../ScrollBlock';
import { ActiveOption } from './ActiveOption';
import { ScDepositWithWageringContent } from './styled';

const OPTIONS_HEIGHTS = {
  WAGERING: 131,
  DEFAULT: 48,
  MAX_ADDON: 144,
  MAX_DEFAULT: 320,
};

interface WithWageringContentProps {
  isInputAddon?: boolean;
  mode?: ThemeMode;
  toggleOpen: () => void;
  onChange: (currency: string) => void;
}

export const WithWageringContent = forwardRef<
  HTMLDivElement,
  WithWageringContentProps
>(({ mode, isInputAddon, onChange, toggleOpen }, ref) => {
  const { t } = useTranslation('deposits');
  const [isEditCurrenciesOpen, setIsEditCurrenciesOpen] = useState(false);

  const useCommonStore = useGetCommonStore();

  const { isMobileDevice, extendedUserWallets, user, wallets, fetchBonuses } =
    useCommonStore(
      useShallow((state) => ({
        isMobileDevice: state.isMobileDevice,
        extendedUserWallets: state.extendedUserWallets,
        user: state.user,
        wallets: state.wallets,
        fetchBonuses: state.fetchBonuses,
      }))
    );

  const toggleEditCurrencies = () => {
    setIsEditCurrenciesOpen((prev) => !prev);
  };

  useEffect(() => {
    fetchBonuses();
  }, [fetchBonuses]);

  const { data: playerAccountsData, isLoading: playerAccountPending } =
    usePlayerAccounts();

  const { activeWageringBonusByCurrency } = useUserWageringBonuses();

  const account = useMemo(() => {
    if (!(playerAccountsData && user)) return;

    const wallet =
      wallets.find(({ currency }) => currency === user.currency) || wallets[0];
    const account =
      playerAccountsData.find(({ currency }) => currency === wallet.currency) ||
      playerAccountsData[0];

    return account;
  }, [playerAccountsData, user, wallets]);

  const options = useMemo(
    () =>
      extendedUserWallets
        .map((wallet) => ({
          ...wallet,
          isActive:
            wallet.currency === user?.currency ||
            extendedUserWallets.length === 1,
        }))
        .sort((a, b) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1)),
    [extendedUserWallets, user?.currency]
  );

  const maxBlockHeight = useMemo(() => {
    if (isMobileDevice) return '100%';

    const activeOptionHeight = activeWageringBonusByCurrency
      ? OPTIONS_HEIGHTS.WAGERING
      : OPTIONS_HEIGHTS.DEFAULT;

    const totalHeight =
      (options.length - 1) * OPTIONS_HEIGHTS.DEFAULT + activeOptionHeight;

    const max = isInputAddon
      ? OPTIONS_HEIGHTS.MAX_ADDON
      : OPTIONS_HEIGHTS.MAX_DEFAULT;

    return `${totalHeight > max ? max : totalHeight}px`;
  }, [
    options.length,
    isInputAddon,
    isMobileDevice,
    activeWageringBonusByCurrency,
  ]);

  return (
    <ScDepositWithWageringContent ref={ref}>
      <ScrollBlock height={maxBlockHeight} mode={mode} scrollTopMargin={0}>
        <ul className="options">
          {options.map((option) =>
            option.isActive ? (
              <ActiveOption
                {...option}
                account={account}
                accountPending={playerAccountPending}
                key={option.currency}
                toggleOpen={toggleOpen}
              />
            ) : (
              <li
                className="option"
                key={option.currency}
                onClick={() => onChange(option.currency)}
              >
                <div className="icon">
                  <IconComponent name="emptyTickCircle" />
                </div>
                <div className="currency">
                  {amountWithCurrencySymbol(
                    option.subCurrency,
                    option.amountCents
                  )}
                  {option.icon && (
                    <ImageComponent
                      alt="currency"
                      height={24}
                      src={option.icon}
                      width={24}
                    />
                  )}
                </div>
              </li>
            )
          )}
        </ul>
      </ScrollBlock>
      <Button
        autoPaddingHorizontal
        className="currencyBtn"
        onClick={toggleEditCurrencies}
        small
        wide
      >
        {options.length > 1 ? t('currencies.edit') : t('currencies.add')}
      </Button>
      {isEditCurrenciesOpen && (
        <EditCurrenciesModal onClose={toggleEditCurrencies} />
      )}
    </ScDepositWithWageringContent>
  );
});
